import { useMemo, useState } from "react";
import { useReveal } from "../lib/useReveal";
import {
    ArrowRight,
    BookOpenText,
    HeartPulse,
    Compass,
    Newspaper,
    X,
    CalendarDays,
    UserRound,
    Clock3,
} from "lucide-react";

const RESOURCES = [
    {
        Icon: BookOpenText,
        eyebrow: "Guides",
        title: "Daily recovery tools you can actually use",
        body: "Grounding prompts, journaling ideas, and check-in rituals that make hard days feel more manageable.",
        href: "#faq",
        cta: "Explore support tools",
        tone: "bg-emerald-100 text-emerald-700",
    },
    {
        Icon: HeartPulse,
        eyebrow: "Support",
        title: "What to do when cravings hit or emotions spike",
        body: "Practical, compassionate guidance for moments that feel urgent, lonely, or overwhelming.",
        href: "#contact",
        cta: "See crisis support",
        tone: "bg-rose-100 text-rose-700",
    },
    {
        Icon: Compass,
        eyebrow: "Find help",
        title: "Move from private reflection to real-world care",
        body: "Use the directory to browse sober living homes, rehabs, outpatient programs, and community resources.",
        href: "/directory",
        cta: "Browse the directory",
        tone: "bg-blue-100 text-blue-700",
    },
];

const BLOGS = [
    {
        title: "How to handle cravings in the first 10 minutes",
        excerpt: "Simple grounding steps you can use immediately when cravings spike.",
        image: "https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?auto=format&fit=crop&w=1200&q=85",
        author: "Genesis Team",
        readTime: "4 min read",
        tags: ["Grounding", "Cravings", "Daily Recovery"],
        details:
            "Cravings wave ki tarah aati hain aur usually 10-15 minute mein low ho jati hain. Is waqt 4 deep breaths, thanda pani, 5-4-3-2-1 grounding exercise, aur kisi trusted person ko short message bhejna bohat madad karta hai.",
        category: "Coping Skills",
        date: "Apr 2026",
    },
    {
        title: "A beginner's daily check-in routine for recovery",
        excerpt: "Build a repeatable 3-minute ritual to stay connected to your progress.",
        image: "https://images.unsplash.com/photo-1474418397713-7ede21d49118?auto=format&fit=crop&w=1200&q=85",
        author: "Hope Content Lab",
        readTime: "5 min read",
        tags: ["Check-ins", "Routine", "Consistency"],
        details:
            "Rozana check-in ko simple rakhein: 1) mood score, 2) craving level, 3) aaj ka focus. Agar aap consistent 2 haftay tak ye routine follow karte hain to emotional patterns clear nazar aate hain aur relapse risk kam hota hai.",
        category: "Daily Practice",
        date: "Apr 2026",
    },
    {
        title: "Journaling prompts for difficult emotions",
        excerpt: "Try these prompts when you feel overwhelmed, anxious, or numb.",
        image: "https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=1200&q=85",
        author: "Recovery Editorial",
        readTime: "6 min read",
        tags: ["Journaling", "Emotions", "Self-Reflection"],
        details:
            "Start with prompts like: 'Main abhi asal mein kya feel kar raha hoon?', 'Mujhe kis cheez ka darr hai?', aur 'Aaj ka ek healthy next step kya ho sakta hai?'. Judgement ke baghair likhna healing ko fast karta hai.",
        category: "Journaling",
        date: "Apr 2026",
    },
    {
        title: "What to do after a hard day (without shame)",
        excerpt: "A gentle reset framework to help you recover emotionally and mentally.",
        category: "Mindset",
        date: "Mar 2026",
    },
    {
        title: "How mentors can support your recovery plan",
        excerpt: "Learn what a healthy mentor relationship can look like in real life.",
        category: "Community",
        date: "Mar 2026",
    },
    {
        title: "Privacy-first recovery tools: what to look for",
        excerpt: "A checklist for choosing apps that actually protect your personal data.",
        category: "Privacy",
        date: "Mar 2026",
    },
    {
        title: "Sleep and sobriety: rebuilding your routine",
        excerpt: "Practical habits that help stabilize sleep during early recovery.",
        category: "Wellbeing",
        date: "Mar 2026",
    },
    {
        title: "Dealing with loneliness on weekends",
        excerpt: "Plan your weekend intentionally so isolation doesn't pull you backward.",
        category: "Support",
        date: "Feb 2026",
    },
    {
        title: "How to prepare for your first support meeting",
        excerpt: "What to expect and how to make your first meeting feel less stressful.",
        category: "Community",
        date: "Feb 2026",
    },
    {
        title: "Relapse warning signs you should not ignore",
        excerpt: "Recognize early patterns and take action before things escalate.",
        category: "Relapse Prevention",
        date: "Feb 2026",
    },
    {
        title: "Building confidence one sober day at a time",
        excerpt: "Small wins compound when you measure progress with compassion.",
        category: "Motivation",
        date: "Feb 2026",
    },
    {
        title: "How to talk to family about your recovery",
        excerpt: "Set boundaries and ask for support in ways that feel clear and safe.",
        category: "Relationships",
        date: "Jan 2026",
    },
    {
        title: "Finding treatment options in your state",
        excerpt: "Use practical filters to compare local recovery programs effectively.",
        category: "Directory",
        date: "Jan 2026",
    },
    {
        title: "Managing stress without substances",
        excerpt: "Healthy alternatives to regulate stress in high-pressure moments.",
        category: "Coping Skills",
        date: "Jan 2026",
    },
    {
        title: "Morning routines that protect your sobriety",
        excerpt: "Start your day with structure to reduce emotional chaos later on.",
        category: "Daily Practice",
        date: "Jan 2026",
    },
    {
        title: "What to do when motivation disappears",
        excerpt: "A fallback recovery plan for low-energy and low-motivation days.",
        category: "Mindset",
        date: "Dec 2025",
    },
    {
        title: "How to use AI support without overdependence",
        excerpt: "Use digital companions as support tools, not replacements for people.",
        category: "AI Companion",
        date: "Dec 2025",
    },
    {
        title: "Recovery milestones: why they matter",
        excerpt: "Celebrate progress in ways that build momentum, not pressure.",
        category: "Progress",
        date: "Dec 2025",
    },
    {
        title: "Creating a personal crisis support plan",
        excerpt: "Prepare contacts, steps, and resources before an emergency moment.",
        category: "Safety",
        date: "Nov 2025",
    },
    {
        title: "How to restart after a setback",
        excerpt: "A practical, shame-free way to get back on track quickly.",
        category: "Relapse Prevention",
        date: "Nov 2025",
    },
];

export default function Resources() {
    const ref = useReveal();
    const [page, setPage] = useState(1);
    const [selectedBlog, setSelectedBlog] = useState(null);
    const pageSize = 6;
    const totalPages = Math.ceil(BLOGS.length / pageSize);
    const paginatedBlogs = useMemo(() => {
        const start = (page - 1) * pageSize;
        return BLOGS.slice(start, start + pageSize);
    }, [page]);

    return (
        <section
            id="resources"
            ref={ref}
            data-testid="resources-section"
            className="py-20 md:py-24 bg-muted/30 border-y scroll-mt-24"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 md:mb-14 reveal">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-5">
                            <Newspaper className="w-4 h-4" />
                            Blog & Resources
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">
                            Helpful recovery content, not content for content&apos;s sake.
                        </h2>
                        <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
                            Keep the tone warm, practical, and hopeful. This section gives visitors
                            reasons to trust the platform before they ever open the app.
                        </p>
                    </div>
                    <a
                        href="#contact"
                        className="inline-flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all"
                    >
                        Get launch updates
                        <ArrowRight className="w-4 h-4" />
                    </a>
                </div>

                <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-6 reveal">
                    <div className="grid md:grid-cols-2 gap-6">
                        {RESOURCES.slice(0, 2).map((item, index) => (
                            <a
                                key={item.title}
                                href={item.href}
                                data-testid={`resource-card-${index}`}
                                className="group rounded-3xl border bg-card p-7 hover:shadow-lg hover:border-primary/30 hover:-translate-y-1 transition-all"
                            >
                                <div
                                    className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl ${item.tone} mb-5`}
                                >
                                    <item.Icon className="w-6 h-6" />
                                </div>
                                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-3">
                                    {item.eyebrow}
                                </div>
                                <h3 className="text-2xl font-bold text-foreground tracking-tight leading-tight">
                                    {item.title}
                                </h3>
                                <p className="mt-4 text-muted-foreground leading-relaxed">
                                    {item.body}
                                </p>
                                <div className="mt-6 inline-flex items-center gap-2 text-primary font-semibold group-hover:gap-3 transition-all">
                                    {item.cta}
                                    <ArrowRight className="w-4 h-4" />
                                </div>
                            </a>
                        ))}
                    </div>

                    <a
                        href={RESOURCES[2].href}
                        data-testid="resource-feature-card"
                        className="group relative overflow-hidden rounded-[2rem] border bg-slate-950 text-white p-8 md:p-10 min-h-[22rem] flex flex-col justify-end hover:-translate-y-1 transition-transform"
                    >
                        <img
                            src="https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?auto=format&fit=crop&w=1200&q=85"
                            alt=""
                            className="absolute inset-0 h-full w-full object-cover opacity-25"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950/25" />
                        <div className="relative z-10">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/10 text-xs uppercase tracking-[0.18em] text-emerald-300 font-semibold mb-4">
                                <Compass className="w-4 h-4" />
                                Find support
                            </div>
                            <h3 className="text-3xl font-bold tracking-tight leading-tight max-w-sm">
                                Private, judgment-free search when you&apos;re ready for more support.
                            </h3>
                            <p className="mt-4 text-white/75 max-w-md leading-relaxed">
                                Frame the directory as a natural next step from Hope: private digital
                                support first, verified real-world resources whenever the moment feels right.
                            </p>
                            <div className="mt-6 inline-flex items-center gap-2 text-white font-semibold group-hover:gap-3 transition-all">
                                {RESOURCES[2].cta}
                                <ArrowRight className="w-4 h-4" />
                            </div>
                        </div>
                    </a>
                </div>

                <div className="mt-14 md:mt-16 reveal">
                    <div className="flex items-center justify-between gap-4 mb-6">
                        <h3 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                            Latest blog posts
                        </h3>
                        <p className="text-sm text-muted-foreground">
                            Showing {paginatedBlogs.length} of {BLOGS.length} blogs
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {paginatedBlogs.map((blog) => (
                            <article
                                key={blog.title}
                                className="rounded-2xl border bg-card p-5 hover:shadow-md transition-shadow"
                            >
                                <div className="flex items-center gap-2 text-xs mb-3">
                                    <span className="px-2.5 py-1 rounded-full bg-primary/10 text-primary font-semibold">
                                        {blog.category}
                                    </span>
                                    <span className="text-muted-foreground">{blog.date}</span>
                                </div>
                                <h4 className="text-lg font-bold text-foreground leading-snug">
                                    {blog.title}
                                </h4>
                                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                                    {blog.excerpt}
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setSelectedBlog(blog)}
                                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all"
                                >
                                    Read more
                                    <ArrowRight className="w-3.5 h-3.5" />
                                </button>
                            </article>
                        ))}
                    </div>

                    <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                        <button
                            type="button"
                            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                            disabled={page === 1}
                            className="h-10 px-4 rounded-full border text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted/40 transition-colors"
                        >
                            Previous
                        </button>
                        {Array.from({ length: totalPages }).map((_, index) => {
                            const pageNumber = index + 1;
                            const isActive = pageNumber === page;
                            return (
                                <button
                                    type="button"
                                    key={pageNumber}
                                    onClick={() => setPage(pageNumber)}
                                    className={`h-10 w-10 rounded-full text-sm font-semibold transition-colors ${
                                        isActive
                                            ? "bg-slate-950 text-white"
                                            : "border hover:bg-muted/40"
                                    }`}
                                >
                                    {pageNumber}
                                </button>
                            );
                        })}
                        <button
                            type="button"
                            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                            disabled={page === totalPages}
                            className="h-10 px-4 rounded-full border text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted/40 transition-colors"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
            {selectedBlog && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
                    <button
                        type="button"
                        aria-label="Close popup"
                        className="absolute inset-0 bg-slate-950/65 backdrop-blur-sm"
                        onClick={() => setSelectedBlog(null)}
                    />
                    <div className="relative w-full max-w-3xl overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl">
                        <div className="relative h-64 md:h-72">
                            <img
                                src={
                                    selectedBlog.image ||
                                    "https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?auto=format&fit=crop&w=1200&q=85"
                                }
                                alt={selectedBlog.title}
                                className="h-full w-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/30 via-slate-950/5 to-transparent" />
                            <div className="absolute top-4 left-4 rounded-md bg-emerald-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-emerald-700">
                                {selectedBlog.category}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setSelectedBlog(null)}
                            className="absolute top-4 right-4 inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 bg-white/95 text-slate-600 hover:text-slate-900"
                            aria-label="Close"
                        >
                            <X className="h-4 w-4" />
                        </button>
                        <div className="p-6 md:p-8">
                            <h3 className="text-3xl font-bold tracking-tight text-foreground pr-8">
                                {selectedBlog.title}
                            </h3>
                            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-600">
                                <span className="inline-flex items-center gap-1.5">
                                    <UserRound className="h-4 w-4" />
                                    {selectedBlog.author || "Genesis Team"}
                                </span>
                                <span className="inline-flex items-center gap-1.5">
                                    <CalendarDays className="h-4 w-4" />
                                    {selectedBlog.date}
                                </span>
                                <span className="inline-flex items-center gap-1.5">
                                    <Clock3 className="h-4 w-4" />
                                    {selectedBlog.readTime || "5 min read"}
                                </span>
                            </div>
                            <p className="mt-5 text-lg text-slate-700 leading-relaxed">
                                {selectedBlog.excerpt}
                            </p>
                            <p className="mt-4 text-[15px] md:text-base text-slate-700 leading-relaxed">
                                {selectedBlog.details ||
                                    "Recovery ek process hai jisme choti choti consistent habits sab se zyada farq laati hain. Is topic par practical steps choose karke daily routine me fit karein."}
                            </p>
                            <div className="mt-5 flex flex-wrap gap-2">
                                {(selectedBlog.tags || ["Recovery", "Support", "Wellbeing"]).map(
                                    (tag) => (
                                        <span
                                            key={tag}
                                            className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600"
                                        >
                                            {tag}
                                        </span>
                                    )
                                )}
                            </div>
                            <div className="mt-6 border-t pt-4 text-sm text-slate-500">
                                Informational only. Recovery journeys are personal - seek professional
                                support when needed.
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
